import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = window.location.protocol === 'https:' ? '/api' : `http://${window.location.hostname}:3010/api`;

const PREDEFINED_TYPES = [
  { value: 'current_state', labelKey: 'typeCurrentState' },
  { value: 'future_vision', labelKey: 'typeFutureVision' },
  { value: 'maturity', labelKey: 'typeMaturity' },
  { value: 'gap_analysis', labelKey: 'typeGapAnalysis' },
];

const TemplateUpload: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const fileRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('current_state');
  const [customType, setCustomType] = useState('');
  const [useCustomType, setUseCustomType] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  // Excel-specific state
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [workbookData, setWorkbookData] = useState<XLSX.WorkBook | null>(null);
  const [isExcel, setIsExcel] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview([]);
    setSheetNames([]);
    setSelectedSheet('');
    setWorkbookData(null);

    const ext = f.name.split('.').pop()?.toLowerCase();

    if (ext === 'xlsx' || ext === 'xls') {
      setIsExcel(true);
      // Read Excel file
      const reader = new FileReader();
      reader.onload = (ev) => {
        const data = new Uint8Array(ev.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        setWorkbookData(wb);
        setSheetNames(wb.SheetNames);
        if (wb.SheetNames.length > 0) {
          setSelectedSheet(wb.SheetNames[0]);
          parseExcelSheet(wb, wb.SheetNames[0]);
        }
      };
      reader.readAsArrayBuffer(f);
    } else {
      setIsExcel(false);
      // Read CSV
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        const rows = parseCSVForPreview(text);
        setPreview(rows);
      };
      reader.readAsText(f, 'utf-8');
    }
  };

  const parseExcelSheet = (wb: XLSX.WorkBook, sheetName: string) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return;
    const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
    if (jsonData.length < 2) {
      setPreview([]);
      return;
    }

    // First row is headers
    const headers = (jsonData[0] || []).map((h: any) => String(h || '').trim());
    const rows: any[] = [];
    for (let i = 1; i < Math.min(jsonData.length, 11); i++) {
      const rowData = jsonData[i];
      if (!rowData || rowData.every(v => !v)) continue;
      const row: any = {};
      headers.forEach((h, idx) => { row[h] = String(rowData[idx] || '').trim(); });
      rows.push(row);
    }
    setPreview(rows);
  };

  const handleSheetChange = (sheetName: string) => {
    setSelectedSheet(sheetName);
    if (workbookData) {
      parseExcelSheet(workbookData, sheetName);
    }
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '\"') {
        if (inQuotes && line[i + 1] === '\"') { current += '\"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) { result.push(current); current = ''; }
      else { current += ch; }
    }
    result.push(current);
    return result;
  };

  const parseCSVForPreview = (text: string): any[] => {
    const lines = text.split('\n');
    if (lines.length < 2) return [];
    const headers = parseCSVLine(lines[0]);
    const rows: any[] = [];
    for (let i = 1; i < Math.min(lines.length, 11); i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const values = parseCSVLine(line);
      const row: any = {};
      headers.forEach((h, idx) => { row[h.trim()] = (values[idx] || '').trim(); });
      rows.push(row);
    }
    return rows;
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) {
      alert(t('templateUpload', 'titleAndFileRequired'));
      return;
    }

    const finalType = useCustomType ? customType.trim() : type;
    if (!finalType) {
      alert(t('templateUpload', 'typeRequired'));
      return;
    }

    try {
      setUploading(true);

      if (isExcel && workbookData) {
        // Convert Excel sheet to CSV and upload
        const ws = workbookData.Sheets[selectedSheet];
        const csvContent = XLSX.utils.sheet_to_csv(ws);
        const csvBlob = new Blob([csvContent], { type: 'text/csv' });
        const csvFile = new File([csvBlob], file.name.replace(/\.xlsx?$/i, '.csv'), { type: 'text/csv' });

        const formData = new FormData();
        formData.append('file', csvFile);
        formData.append('title', title.trim());
        formData.append('type', finalType);

        const res = await fetch(`${API_URL}/survey-templates/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error?.message || `Error: ${res.status}`);
        }
      } else {
        // CSV upload directly
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', title.trim());
        formData.append('type', finalType);

        const res = await fetch(`${API_URL}/survey-templates/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error?.message || `Error: ${res.status}`);
        }
      }

      navigate('/admin/templates');
    } catch (err: any) {
      alert(t('templateUpload', 'uploadFailed') + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ padding: '30px', maxWidth: '900px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '24px' }}>{t('templateUpload', 'title')}</h2>

      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>{t('templateUpload', 'titleLabel')}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('templateUpload', 'titlePlaceholder')}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '1rem', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>{t('templateUpload', 'typeLabel')}</label>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            {!useCustomType ? (
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '1rem' }}
              >
                {PREDEFINED_TYPES.map(pt => (
                  <option key={pt.value} value={pt.value}>{t('template', pt.labelKey)}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                placeholder={t('templateUpload', 'customTypePlaceholder')}
                style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '1rem', minWidth: '200px' }}
              />
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: '#4a5568', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={useCustomType}
                onChange={(e) => setUseCustomType(e.target.checked)}
              />
              {t('templateUpload', 'customType')}
            </label>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>{t('templateUpload', 'fileLabel')}</label>
          <div style={{ padding: '20px', border: '2px dashed #e2e8f0', borderRadius: '8px', textAlign: 'center', cursor: 'pointer' }}
            onClick={() => fileRef.current?.click()}
          >
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} />
            {file ? (
              <p style={{ margin: 0, color: '#2c5282' }}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
            ) : (
              <p style={{ margin: 0, color: '#a0aec0' }}>{t('templateUpload', 'filePrompt')}</p>
            )}
          </div>
          <p style={{ fontSize: '0.8rem', color: '#718096', marginTop: '8px' }}>
            {t('templateUpload', 'headerNote')}
          </p>
        </div>

        {/* Sheet Selection for Excel */}
        {isExcel && sheetNames.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>{t('templateUpload', 'sheetSelection')}</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {sheetNames.map((name, idx) => (
                <button
                  key={name}
                  onClick={() => handleSheetChange(name)}
                  style={{
                    padding: '8px 14px',
                    border: selectedSheet === name ? '2px solid #2c5282' : '1px solid #e2e8f0',
                    borderRadius: '6px',
                    backgroundColor: selectedSheet === name ? '#ebf8ff' : '#fff',
                    color: selectedSheet === name ? '#2c5282' : '#4a5568',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: selectedSheet === name ? 600 : 400,
                  }}
                >
                  {idx + 1}. {name}
                </button>
              ))}
            </div>
          </div>
        )}

        {preview.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 10px' }}>{t('templateUpload', 'preview')}</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f7fafc' }}>
                    {Object.keys(preview[0]).map(key => (
                      <th key={key} style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => (
                        <td key={j} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {val as string}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleUpload}
            disabled={uploading || !file || !title.trim()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2c5282',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: uploading || !file || !title.trim() ? 0.6 : 1
            }}
          >
            {uploading ? t('templateUpload', 'uploading') : t('templateUpload', 'upload')}
          </button>
          <button
            onClick={() => navigate('/admin/templates')}
            style={{ padding: '12px 24px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem', color: '#4a5568' }}
          >
            {t('common', 'cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateUpload;
